<template>
  <div>
    <reg-modal-manager v-if="canCreateNew && detailsModalName"
                       ref="modalManager"
                       @created="onSelectCreatedEditedItem"
                       @edited="onSelectCreatedEditedItem"
                       @close="onCloseModal" />
    <v-autocomplete v-if="!readOnly && !hideSearch"
                    ref="autocomplete"
                    :search-input="search"
                    :items="items"
                    :disabled="disabled"
                    :clearable="clearable"
                    :single-line="singleLine"
                    :value="selectedItem"
                    :label="label"
                    :prepend-icon="icon"
                    :item-value="itemValue"
                    :item-text="itemText"
                    :return-object="true"
                    :loading="$isLoading(loader)"
                    :filled="box"
                    :hint="hint"
                    :placeholder="dynamicPlaceholder"
                    :chips="chips"
                    :required="required"
                    :rules="rules"
                    no-filter
                    persistent-hint
                    @blur="onFieldBlur"
                    @focus="onFieldExpand"
                    @update:search-input="search = $event"
                    @input="updateValue">
      <template v-if="canCreateNew" #append-outer>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-icon color="primary"
                    v-on="on"
                    @click="onNewClick">
              mdi-plus
            </v-icon>
          </template>
          <span>Novo</span>
        </v-tooltip>
      </template>
      <template #no-data>
        <v-list-item>
          <v-list-item-title>
            Nenhum resultado encontrado.
          </v-list-item-title>
          <v-list-item-action v-if="canCreateNew">
            <v-btn outlined color="primary" class="mr-3" @click="onNewClick">
              <v-icon>mdi-plus</v-icon>
              Novo
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </template>
      <template #selection="data">
        <v-chip v-if="chips"
                :value="data.selected"
                class="chip--select-multi">
          {{ lodash.get(data.item, itemText) }}
        </v-chip>
        <span v-else>{{ lodash.get(data.item, itemText) }}</span>
      </template>
      <template #item="data">
        <template>
          <v-list-item-content>
            <v-list-item-title>
              {{ lodash.get(data.item, itemText) }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="itemSubTitlePaths?.length">
              {{ itemSubTitlePaths.map(path => lodash.get(data.item, path)).filter(i => !!i).join(' - ') }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="showPreviewButton">
            <v-tooltip left>
              <template #activator="{ on, attrs }">
                <v-btn icon
                       small
                       v-bind="attrs"
                       v-on="on"
                       @click.stop="onDetailsPreview(data.item)">
                  <v-icon color="primary">
                    mdi-eye
                  </v-icon>
                </v-btn>
              </template>
              <span>Abrir detalhes</span>
            </v-tooltip>
          </v-list-item-action>
        </template>
      </template>
      <template v-if="items?.length === computedLimitFirst" #append-item>
        <v-list-item>
          <v-list-item-title>
            * Exibindo os primeiros {{ computedLimitFirst }} resultados.
          </v-list-item-title>
          <v-list-item-action>
            <v-btn outlined color="primary" class="reg-btn-lower px-2 mr-2" @click="increaseLimitBy(10)">
              Carregar mais 10
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </template>
    </v-autocomplete>
    <v-select v-else-if="!readOnly && hideSearch"
              ref="select"
              :items="items"
              :disabled="disabled"
              :clearable="clearable"
              :single-line="singleLine"
              :value="selectedItem"
              :label="label"
              :prepend-icon="icon"
              :item-value="itemValue"
              :item-text="itemText"
              :return-object="true"
              :loading="$isLoading(loader)"
              :filled="box"
              :hint="hint"
              :placeholder="dynamicPlaceholder"
              :chips="chips"
              :required="required"
              :rules="rules"
              no-filter
              persistent-hint
              @blur="onFieldBlur"
              @focus="onFieldExpand"
              @input="updateValue">
      <template v-if="chips" #selection="data">
        <v-chip :value="data.selected"
                class="chip--select-multi">
          {{ lodash.get(data.item, itemText) }}
        </v-chip>
      </template>
      <template v-else #selection="data">
        {{ lodash.get(data.item, itemText) }}
      </template>
      <template #item="data">
        <template>
          <v-list-item-content>
            <v-list-item-title>
              {{ lodash.get(data.item, itemText) }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="itemSubTitlePaths?.length">
              {{ itemSubTitlePaths.map(path => lodash.get(data.item, path)).join(' - ') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </template>
    </v-select>
    <div v-else :class="{'reg-information': !small, 'reg-information-small': small}">
      <label>{{ label }}</label>
      <p>{{ lodash.get(selectedItem, itemText) }}</p>
    </div>
  </div>
</template>

<script>
import ModalManager from '~shared/ModalManager.vue'
import { v4 as uuid } from 'uuid'
import _ from 'lodash'

export default {
  name: 'RegAsyncSelector',
  components: {
    'reg-modal-manager': ModalManager
  },
  props: {
    getMethod: {
      type: Function,
      required: true
    },
    getByIdMethod: {
      type: Function
    },
    orderByKey: {
      type: String,
      default: ''
    },
    orderByDesc: {
      type: Boolean,
      default: false
    },
    limitFirst: {
      type: Number,
      default: 10
    },
    itemValue: {
      type: String,
      default: 'id'
    },
    itemText: {
      type: String,
      default: 'name'
    },
    itemSubTitlePaths: {
      type: Array,
      default: () => []
    },
    searchPaths: {
      type: Array,
      default: () => []
    },
    fixedFilter: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: [String, Array],
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    singleLine: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    chips: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    hideSearch: {
      type: Boolean,
      default: false
    },
    showPreviewButton: {
      type: Boolean,
      default: false
    },
    previeldIdParameterName: {
      type: String,
      default: 'id'
    },
    canCreateNew: {
      type: Boolean,
      default: false
    },
    detailsModalName: {
      type: String,
      default: ''
    },
    newItemModalProps: {
      type: Object,
      default: () => ({})
    },
    small: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loader: uuid(),
      itemsList: [],
      selectedItem: null,
      search: '',
      lodash: _,
      internalLimitFirst: 0,
      dataLoaded: false,        // Flag para controlar se lista completa foi carregada
      selectedItemLoaded: false, // Flag para controlar se item selecionado foi carregado
      skipSearch: false
    }
  },
  computed: {
    items() {
      return this.selectedItem
        ? [this.selectedItem, ...this.itemsList]
        : this.itemsList
    },
    selectedItemText() {
      return _.get(this.selectedItem, this.itemText)
    },
    dynamicPlaceholder() {
      if (this.placeholder) {
        return this.placeholder
      }

      // Loading state
      if (this.$isLoading(this.loader)) {
        return 'Carregando...'
      }

      // Lazy loading states
      if (!this.dataLoaded && !this.selectedItemLoaded) {
        return this.hideSearch ? 'Clique para carregar opções' : 'Clique para carregar opções'
      }

      // Default states after data loaded
      return this.hideSearch ? 'Selecione uma opção' : 'Digite para buscar'
    },
    computedLimitFirst() {
      return this.internalLimitFirst || this.limitFirst
    },
    hasError() {
      return this.$refs.autocomplete?.hasError || this.$refs.select?.hasError
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val, oldVal) {
        if (!val) {
          this.selectedItem = null
        }
        if (val !== oldVal) {
          this.initialFetchData(val)
        }
      }
    },
    search: _.debounce(function (val) {
      if (!this.skipSearch && val !== this.selectedItemText && !_.isObject(val)) {
        this.fetchData()
      }
      this.skipSearch = false
    }, 300)
  },
  methods: {
    async initialFetchData(id) {
      // LAZY LOADING: Só carrega dados quando há valor selecionado
      if (!id) {
        this.selectedItem = null
        this.selectedItemLoaded = false
        return
      }

      // Se já temos o item na lista carregada, usa ele
      const item = this.itemsList?.find(s => _.get(s, this.itemValue) === id)
      if (!_.isEmpty(item)) {
        this.selectedItem = item
        this.selectedItemLoaded = true
        return
      }

      // Se tem getByIdMethod, carrega apenas o item selecionado (lazy loading)
      if (this.getByIdMethod && !this.selectedItemLoaded) {
        try {
          this.$startLoading(this.loader)
          this.selectedItem = await this.getByIdMethod(id)
          this.selectedItemLoaded = true
        } catch (error) {
          console.error('Erro ao carregar item selecionado:', error)
        } finally {
          this.$endLoading(this.loader)
        }
        return
      }

      // Fallback: carrega lista completa (comportamento antigo para compatibilidade)
      if (!this.dataLoaded) {
        await this.fetchData()
        const foundItem = this.itemsList?.find(s => _.get(s, this.itemValue) === id)
        if (!_.isEmpty(foundItem)) {
          this.selectedItem = foundItem
          this.selectedItemLoaded = true
        }
      }
    },
    async fetchData() {
      try {
        this.$startLoading(this.loader)
        if (this.hideSearch) {
          const res = await this.getMethod()
          this.itemsList = res
        } else {
          const orderBy = this.orderByKey ? `${this.orderByKey}${this.orderByDesc ? '_DESC' : '_ASC'}` : null
          const offset = 0
          const gqlFilter = {
          }

          if (this.search) {
            if (this.searchPaths?.length) {
              gqlFilter.or = []

              this.searchPaths.forEach(path => {
                const filter = {}
                _.set(filter, path, this.search)
                gqlFilter.or.push(filter)
              })
            } else if (this.itemText) {
              gqlFilter[this.itemText] = {
                includesInsensitive: this.search
              }
            }
          }

          if (this.fixedFilter) {
            _.merge(gqlFilter, this.fixedFilter)
          }

          const res = await this.getMethod({
            filter: _.isEmpty(gqlFilter) ? undefined : gqlFilter,
            first: this.computedLimitFirst,
            offset,
            orderBy
          })
          this.itemsList = res
        }

        // Marcar que os dados foram carregados
        this.dataLoaded = true
      } finally {
        this.$endLoading(this.loader)
      }
    },
    onFieldBlur() {
      if (this.search) {
        this.skipSearch = true
        this.dataLoaded = false
        this.search = ''
      }
    },
    async onFieldExpand() {
      // Carregar dados completos apenas quando usuário expandir o campo
      if (!this.dataLoaded && !this.$isLoading(this.loader)) {
        await this.fetchData()
      }
    },
    updateValue(value) {
      this.internalLimitFirst = 0
      this.selectedItem = value
      this.$emit('selected-text', _.get(value, this.itemText))
      this.$emit('input', _.get(value, this.itemValue))
    },
    onDetailsPreview(item) {
      this.$emit('preview', item)
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.blur()
      } else if (this.$refs.select) {
        this.$refs.select.blur()
      }

      if (this.detailsModalName) {
        this.$refs.modalManager.openModal(this.detailsModalName, {
          ...this.newItemModalProps,
          [this.previeldIdParameterName]: _.get(item, this.itemValue),
          readOnly: true
        })
      }
    },
    onNewClick() {
      this.$emit('new')
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.blur()
      } else if (this.$refs.select) {
        this.$refs.select.blur()
      }
      if (this.detailsModalName) {
        this.$refs.modalManager.openModal(this.detailsModalName, this.newItemModalProps)
      }
    },
    onSelectCreatedEditedItem(modalName, item) {
      this.selectedItem = item
      this.$emit('input', _.get(item, this.itemValue))
    },
    onCloseModal() {
      this.$emit('close-details-modal')
      this.fetchData()
    },
    increaseLimitBy(num) {
      if (!this.internalLimitFirst) {
        this.internalLimitFirst = this.limitFirst
      }
      this.internalLimitFirst = this.internalLimitFirst + num
      this.fetchData()
    }
  }
}
</script>
